import React, { useEffect, useMemo } from "react";
import {
  Node,
  Edge,
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  MarkerType,
  useNodesState,
  ReactFlowInstance,
  NodeTypes
} from "@xyflow/react";
import ELK from "elkjs/lib/elk.bundled.js";
import "@xyflow/react/dist/style.css";
import { FunctionNodeUI, PromptNodeUI } from "./PromptGraphNodes";
import { TPromptGraphData, TPromptGraphJoinNode } from "../../../model/promptGraphTypes";

const nodeTypes: NodeTypes = {
  promptNode: PromptNodeUI,
  functionNode: FunctionNodeUI
};

const estimateSize = (label: string, kind: "prompt" | "func") => {
  // very rough character-based width, good enough for ELK pre-layout
  const charW = 8; // avg px per char
  const padX = 24; // left+right
  const padY = 18; // top+bottom
  const minW = kind === "prompt" ? 160 : 120;
  const minH = kind === "prompt" ? 64 : 44;
  const lines = label.split("\n");
  const maxLine = Math.max(...lines.map((l) => l.length), 1);
  const w = Math.max(minW, maxLine * charW + padX);
  const h = Math.max(minH, lines.length * 18 + padY);
  return { width: w, height: h };
};

function toFlowElements<Slug extends string>(g: TPromptGraphData<Slug>) {
  const promptIds = Object.keys(g.nodes).map(String);
  const nodeIdSet = new Set(promptIds);

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  for (const key in g.nodes) {
    const n = g.nodes[key];
    const id = String(n.slug);
    nodes.push({
      id,
      type: "promptNode",
      data: {
        label: id,
        prompt: n.prompt,
        result: n.result,
        webSearchNeeded: n.webSearchNeeded,
        usedFunctions: n.usedFunctions,
        isRoot: id === g.rootNode,
        context: g.context
      },
      position: { x: 0, y: 0 },
      draggable: true
    });

    const outs = Array.isArray(n.usedNodes) ? n.usedNodes : [];
    for (const t of outs) {
      const tgt = String(t);
      if (!nodeIdSet.has(tgt)) continue;
      edges.push({
        id: `${id}->${tgt}`,
        source: id,
        target: tgt,
        markerEnd: { type: MarkerType.ArrowClosed }
      });
    }
  }

  const functionNames = new Set<string>(Object.keys(g.uniqueFunctionCalls || {}));
  for (const fn of functionNames) {
    const fnId = `fn:${fn}`;
    const result = g.uniqueFunctionCalls?.[fn]?.result;
    nodes.push({
      id: fnId,
      type: "functionNode",
      data: { label: fn, result },
      position: { x: 0, y: 0 }
    });
  }

  for (const key in g.nodes) {
    const n = g.nodes[key];
    const src = String(n.slug);
    for (const fn of n.usedFunctions || []) {
      const fnId = `fn:${String(fn)}`;
      edges.push({
        id: `${src}=>${fnId}`,
        source: src,
        target: fnId,
        markerEnd: { type: MarkerType.ArrowClosed },
        style: { strokeDasharray: "4 3" }
      });
    }
  }

  return { nodes, edges };
}

async function elkLayout(nodes: Node[], edges: Edge[]) {
  const elk = new ELK();
  const children = nodes.map((n) => {
    const isFunc = n.type === "functionNode";
    const { width, height } = estimateSize(String(n.data?.label ?? ""), isFunc ? "func" : "prompt");
    return { id: n.id, width, height };
  });
  const g = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "RIGHT",
      "elk.layered.nodePlacement.strategy": "BRANDES_KOEPF",
      "elk.layered.spacing.nodeNodeBetweenLayers": "300",
      "elk.spacing.nodeNode": "150"
    },
    children,
    edges: edges.map((e) => ({ id: e.id, sources: [e.source], targets: [e.target] }))
  };
  const layout = await elk.layout(g);
  const posMap = new Map(layout.children?.map((c) => [c.id, c]));
  return nodes.map((n) => {
    const p = posMap.get(n.id);
    return { ...n, position: { x: p?.x ?? 0, y: p?.y ?? 0 } };
  });
}

type TPromptGraphViewerProps<Slug extends string> = {
  data: TPromptGraphData<Slug>;
  onNodeClick?: (node: TPromptGraphJoinNode) => void;
};

export function PromptGraphViewer<Slug extends string>({ data, onNodeClick }: TPromptGraphViewerProps<Slug>) {
  const { nodes: initialNodes, edges } = useMemo(() => {
    try {
      return toFlowElements(data);
    } catch (error) {
      console.error("Failed to process graph data:", error);
      return { nodes: [], edges: [] };
    }
  }, [data]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const rfRef = React.useRef<ReactFlowInstance | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const laidOut = await elkLayout(initialNodes, edges);
      if (!cancelled) {
        setNodes(laidOut);
        requestAnimationFrame(() => rfRef.current?.fitView({ padding: 0.2 }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialNodes, edges, setNodes]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      fitView
      onInit={(inst) => (rfRef.current = inst)}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      defaultEdgeOptions={{
        markerEnd: { type: MarkerType.ArrowClosed }
      }}
      onNodeClick={(e, node) => {
        if (node.type === "functionNode")
          onNodeClick?.({
            ...data.uniqueFunctionCalls?.[node.data.label as string],
            context: data.context
          });
        else onNodeClick?.({ ...data.nodes[node.id as Slug], context: data.context });
      }}
    >
      <MiniMap zoomable pannable />
      <Controls />
      <Background />
    </ReactFlow>
  );
}
